import React, { useState, useRef, useEffect } from 'react';
import { SpellingGameState } from '../domain/types';
import StarMeter from './StarMeter';
import './SpellingTrace.css';

interface SpellingTraceProps {
  gameState: SpellingGameState;
  onLetterTraced: (letterIndex: number) => void;
  onComplete: () => void;
  reducedMotion?: boolean;
  totalStars: number;
}

const SpellingTrace: React.FC<SpellingTraceProps> = ({
  gameState,
  onLetterTraced,
  onComplete,
  reducedMotion = false,
  totalStars
}) => {
  const [currentLetter, setCurrentLetter] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tracedLetters, setTracedLetters] = useState<boolean[]>(new Array(4).fill(false));
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [paths, setPaths] = useState<{ letter: number; points: { x: number; y: number }[] }[]>([]);

  const targetWord = 'CATA';
  const letters = targetWord.split('');

  const getLetterPath = (letter: string, index: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return [];

    const ctx = canvas.getContext('2d');
    if (!ctx) return [];

    // const rect = canvas.getBoundingClientRect();

    // Define letter paths (simplified)
    const letterPaths: { [key: string]: { x: number; y: number }[] } = {
      'C': [
        { x: 0.2, y: 0.2 }, { x: 0.1, y: 0.2 }, { x: 0.1, y: 0.8 }, { x: 0.2, y: 0.8 }
      ],
      'A': [
        { x: 0.1, y: 0.8 }, { x: 0.2, y: 0.2 }, { x: 0.3, y: 0.2 }, { x: 0.4, y: 0.8 },
        { x: 0.15, y: 0.5 }, { x: 0.35, y: 0.5 }
      ],
      'T': [
        { x: 0.1, y: 0.2 }, { x: 0.4, y: 0.2 }, { x: 0.25, y: 0.2 }, { x: 0.25, y: 0.8 }
      ]
    };

    return letterPaths[letter] || [];
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDrawing(true);
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e as React.MouseEvent).clientX ? 
      (e as React.MouseEvent).clientX - rect.left : 
      (e as React.TouchEvent).touches[0].clientX - rect.left;
    const y = (e as React.MouseEvent).clientY ? 
      (e as React.MouseEvent).clientY - rect.top : 
      (e as React.TouchEvent).touches[0].clientY - rect.top;

    setPaths(prev => [...prev, { letter: currentLetter, points: [{ x, y }] }]);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e as React.MouseEvent).clientX ? 
      (e as React.MouseEvent).clientX - rect.left : 
      (e as React.TouchEvent).touches[0].clientX - rect.left;
    const y = (e as React.MouseEvent).clientY ? 
      (e as React.MouseEvent).clientY - rect.top : 
      (e as React.TouchEvent).touches[0].clientY - rect.top;

    setPaths(prev => {
      const newPaths = [...prev];
      if (newPaths.length > 0) {
        newPaths[newPaths.length - 1].points.push({ x, y });
      }
      return newPaths;
    });
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    
    // Check if current letter is traced
    checkLetterCompletion();
  };

  const checkLetterCompletion = () => {
    // Simple check - if user drew something, consider it traced
    const currentPaths = paths.filter(p => p.letter === currentLetter);
    if (currentPaths.length > 0) {
      const newTraced = [...tracedLetters];
      newTraced[currentLetter] = true;
      setTracedLetters(newTraced);
      onLetterTraced(currentLetter);
      
      if (currentLetter < letters.length - 1) {
        setCurrentLetter(prev => prev + 1);
      } else {
        // All letters traced
        setTimeout(() => onComplete(), 1000);
      }
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setPaths([]);
  };

  // Draw paths on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw current letter outline
    ctx.strokeStyle = '#DCC7F8';
    ctx.lineWidth = 4;
    ctx.setLineDash([5, 5]);
    
    const letterPath = getLetterPath(letters[currentLetter], currentLetter);
    if (letterPath.length > 0) {
      ctx.beginPath();
      ctx.moveTo(letterPath[0].x * canvas.width, letterPath[0].y * canvas.height);
      letterPath.forEach(point => {
        ctx.lineTo(point.x * canvas.width, point.y * canvas.height);
      });
      ctx.stroke();
    }
    
    // Draw user paths
    ctx.setLineDash([]);
    ctx.lineWidth = 6;
    ctx.strokeStyle = '#F4C542';
    
    paths.forEach(path => {
      if (path.points.length > 1) {
        ctx.beginPath();
        ctx.moveTo(path.points[0].x, path.points[0].y);
        path.points.forEach(point => {
          ctx.lineTo(point.x, point.y);
        });
        ctx.stroke();
      }
    });
  }, [paths, currentLetter, letters]);

  return (
    <div className="spelling-trace">
      <div className="trace-header">
        <h2>Traza la letra: {letters[currentLetter]}</h2>
        <div className="trace-progress-info">
          <div className="trace-progress-text">
            Estrellitas: {totalStars}/5
          </div>
          <StarMeter 
            current={totalStars} 
            total={5}
          />
        </div>
        <div className="progress">
          {letters.map((letter, index) => (
            <div
              key={index}
              className={`progress-letter ${index === currentLetter ? 'current' : ''} ${tracedLetters[index] ? 'completed' : ''}`}
            >
              {letter}
            </div>
          ))}
        </div>
      </div>

      <div className="trace-canvas-container">
        <canvas
          ref={canvasRef}
          width={400}
          height={300}
          className="trace-canvas"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <button className="clear-button" onClick={clearCanvas}>
          🗑️ Limpiar
        </button>
      </div>

      {gameState.isCompleted && (
        <div className="completion-celebration">
          <img 
            src="/assets/Celebration.png" 
            alt="Cata celebrando" 
            className="celebration-image"
          />
          <div className="celebration-text">
            ¡Excelente, Cata! Has trazado tu nombre ✨👑
          </div>
        </div>
      )}
    </div>
  );
};

export default SpellingTrace;
